import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { ApiClient } from '../api'
import { formatDateRange, parseIsoDate } from '../lib/dates'
import { Icon } from './Icon'
import type { BuildChecklistStep, EventListItem } from '../types'

// OverviewTab — spec 05. Two-column layout:
//   left  = Key Information + Build Checklist
//   right = Quick Stats + Recent Activity + (Linked Documents — deferred,
//           needs the /api/events/{id}/documents endpoint that spec 05 §6
//           describes but isn't built yet).
//
// Build Checklist gates the 10 steps on event.progress thresholds —
// per spec 05 §6 those thresholds are frontend constants, not data the
// API computes. Move to a server-computed map only if product later
// asks for per-step overrides.
//
// Helpers (KV / Stat / BuildItem / ActivityRow) inlined here rather than
// extracted to src/components/overview/ — spec 05 §5 suggests a barrel,
// but we don't have other consumers yet. Extract when a second tab reuses
// Stat.

interface Props {
  event: EventListItem
}

export function OverviewTab({ event }: Props) {
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="space-y-5">
        <KeyInformationCard event={event} />
        <BuildChecklistCard eventId={event.id} />
      </div>
      <div className="space-y-5">
        <QuickStatsCard event={event} />
        <RecentActivityCard eventId={event.id} />
      </div>
    </div>
  )
}

// ---- Key Information card ------------------------------------------------

function KeyInformationCard({ event }: { event: EventListItem }) {
  // Operating days = the count of the event's actual operating days (its
  // EventDates set), matching the dates shown on the Events-list card — NOT the
  // calendar span between start and end (a multi-date event like Tinsel Tipple
  // runs ~4 days across a fortnight, not 26). Falls back to the inclusive
  // envelope span only if the date set is somehow empty.
  const operatingDays = event.dates.length || daysInclusive(event.startDate, event.endDate)
  return (
    <div className="bg-white border border-line rounded">
      <div className="px-4 py-3 border-b border-line">
        <h3 className="font-display text-lg">Key Information</h3>
      </div>
      <div className="px-4">
        <KV label="Event name" value={event.name} />
        <KV
          label="Event type"
          value={
            <span
              className="inline-flex px-2 py-0.5 rounded text-xs font-semibold"
              style={{ background: event.type.colourBg, color: event.type.colourText }}
            >
              {event.type.name}
            </span>
          }
        />
        <KV label="Dates" value={formatDateRange(event.startDate, event.endDate)} />
        <KV label="Operating days" value={`${operatingDays} day${operatingDays !== 1 ? 's' : ''}`} />
        <KV label="Location" value={event.location ?? <Muted />} />
        <KV
          label="Capacity"
          value={event.capacity ? `${event.capacity.toLocaleString('en-GB')} visitors` : <Muted />}
        />
        <KV
          label="Event manager"
          value={
            event.manager
              ? (
                <div className="flex items-center gap-2">
                  <div
                    className="w-6 h-6 rounded-full text-white text-[10px] font-bold flex items-center justify-center"
                    style={{ background: 'linear-gradient(135deg, #9b7f2a 0%, #c9a13a 100%)' }}
                  >
                    {event.manager.avatarInitials ?? '?'}
                  </div>
                  <span>{event.manager.name ?? 'Unnamed user'}</span>
                </div>
              )
              : <Muted />
          }
        />
        <KV
          label="Build progress"
          value={
            <div className="flex items-center gap-2.5">
              <div className="w-32 h-2 rounded-full bg-line overflow-hidden">
                <div
                  className="h-full bg-green"
                  style={{ width: `${Math.min(100, Math.max(0, event.progress))}%` }}
                />
              </div>
              <span className="font-mono text-sm font-semibold">{event.progress}%</span>
            </div>
          }
          last
        />
      </div>
    </div>
  )
}

// ---- Build Checklist card ------------------------------------------------
//
// Tweak T4: 10 steps each derived from real table state. Replaces the
// cosmetic event.progress >= step.threshold logic that the labels were
// lying about (e.g. "Components selected" was driven by a progress
// number, not by COUNT(Components)). Each step's `done` is computed
// server-side - see api/src/handlers/buildChecklist.ts for the rules.
// Steps 7-9 (volunteer briefings / marketing / walkthrough) come back
// as reason='tbd' until Chris specs them; they render dim and never
// drag down percentComplete.

function BuildChecklistCard({ eventId }: { eventId: number }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['build-checklist', eventId],
    queryFn: () => ApiClient.buildChecklist(eventId),
    staleTime: 30_000
  })

  return (
    <div className="bg-white border border-line rounded">
      <div className="px-4 py-3 border-b border-line flex items-baseline gap-2">
        <h3 className="font-display text-lg flex-1">Build Checklist</h3>
        <span className="text-xs text-ink-soft">
          {isLoading || !data ? '—' : `${data.percentComplete}% complete`}
        </span>
      </div>
      <div className="px-4">
        {isLoading && (
          <div className="py-4 text-xs text-ink-soft font-mono">Loading…</div>
        )}
        {error && (
          <div className="py-3 text-xs text-oxblood">
            Couldn’t load checklist: {(error as Error).message}
          </div>
        )}
        {data?.steps.map((s, i) => (
          <BuildItem
            key={s.slug}
            step={s}
            last={i === data.steps.length - 1}
          />
        ))}
      </div>
    </div>
  )
}

function BuildItem({ step, last }: { step: BuildChecklistStep; last?: boolean }) {
  const isTbd = step.reason === 'tbd'
  return (
    <div
      className="flex items-center gap-2.5 py-2"
      style={last ? undefined : { borderBottom: '1px solid #ece6db' }}
    >
      <div
        className={
          'w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ' +
          (step.done
            ? 'bg-green border-green text-parchment'
            : 'border-line bg-white')
        }
        style={step.done ? { background: '#0a4a3a', borderColor: '#0a4a3a', color: '#f5f1ea' } : undefined}
      >
        {step.done && <Icon name="check-circle" size={11} />}
      </div>
      <span
        className={
          'text-sm flex-1 ' +
          (step.done ? 'text-ink line-through' : isTbd ? 'text-ink-soft/60 italic' : 'text-ink-soft')
        }
      >
        {step.label}
      </span>
      {isTbd && (
        <span className="text-[9px] uppercase tracking-wider font-semibold text-ink-soft/70 bg-parchment border border-line rounded px-1.5 py-0.5">
          TBD
        </span>
      )}
    </div>
  )
}

// ---- Quick Stats card ----------------------------------------------------
//
// Spec 05 §4.3 wants "Tickets sold", "% of capacity", "Days to event",
// "Roster filled". Tickets/roster sources aren't wired into useEvent
// today — ticketsSold lives in MeracSessions (post-PR-#8), roster %
// lives in spec 16's roster service. For now we show what's available
// from event itself: days-to-event, capacity, progress, status. When
// the data sources land, swap these out without changing the card
// structure.

function QuickStatsCard({ event }: { event: EventListItem }) {
  const days = daysFromToday(event.startDate)
  const daysLabel =
    event.status === 'Complete' ? 'Past' :
    event.status === 'Cancelled' ? 'Cancelled' :
    days > 0 ? `${days}` :
    event.status === 'Live' ? 'Live' :
    days === 0 ? 'Today' : 'Past'

  return (
    <div className="bg-white border border-line rounded">
      <div className="px-4 py-3 border-b border-line">
        <h3 className="font-display text-lg">Quick Stats</h3>
      </div>
      <div className="grid grid-cols-2 gap-3 p-4">
        <Stat label="Days to event" value={daysLabel} />
        <Stat
          label="Capacity"
          value={event.capacity ? event.capacity.toLocaleString('en-GB') : '—'}
        />
        <Stat label="Build complete" value={`${event.progress}%`} />
        <Stat label="Status" value={event.status} />
      </div>
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded p-3" style={{ background: '#f5edd5' }}>
      <div className="text-[10px] uppercase tracking-wider font-semibold text-ink-soft mb-1">
        {label}
      </div>
      <div className="font-display text-xl font-bold leading-none">{value}</div>
    </div>
  )
}

// ---- Recent Activity card ------------------------------------------------

interface AuditEntry {
  id: number
  occurredAt: string
  actor: { id: number; name: string | null; avatarInitials: string | null } | null
  entityType: string
  entityId: number | null
  action: string
  summary: string
}

function RecentActivityCard({ eventId }: { eventId: number }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['event-audit', eventId, 'overview'],
    queryFn: () => fetchEventAudit(eventId, 6),
    staleTime: 30_000
  })

  return (
    <div className="bg-white border border-line rounded">
      <div className="px-4 py-3 border-b border-line flex items-baseline gap-2">
        <h3 className="font-display text-lg flex-1">Recent Activity</h3>
        <Link to={`/events/${eventId}/audit`} className="text-xs text-green hover:underline" onClick={(e) => e.preventDefault()}>
          {/* Spec 29 surfaces the full per-event audit — link is preserved
              for the "see all" affordance, but currently no per-event audit
              route exists, so we prevent navigation. Swap to a real link
              when /events/:id/audit ships. */}
          See all
        </Link>
      </div>
      {isLoading ? (
        <div className="px-4 py-4 text-xs text-ink-soft font-mono">Loading…</div>
      ) : error ? (
        <div className="px-4 py-4 text-xs text-oxblood">
          Could not load activity: {(error as Error).message}
        </div>
      ) : !data || data.length === 0 ? (
        <div className="px-4 py-4 text-xs italic text-ink-soft">
          No activity yet. Edits to this event will show up here.
        </div>
      ) : (
        <div>
          {data.slice(0, 6).map((a, i) => (
            <ActivityRow
              key={a.id}
              who={a.actor?.name ?? 'System'}
              what={a.summary}
              when={relativeTime(a.occurredAt)}
              last={i === Math.min(5, data.length - 1)}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function ActivityRow({
  who, what, when, last
}: {
  who: string
  what: string
  when: string
  last?: boolean
}) {
  return (
    <div
      className="px-4 py-2.5"
      style={last ? undefined : { borderBottom: '1px solid #ece6db' }}
    >
      <div className="text-sm">
        <span className="font-semibold">{who}</span>{' '}
        <span className="text-ink-soft">{what}</span>
      </div>
      <div className="text-[11px] text-ink-soft mt-0.5 font-mono">{when}</div>
    </div>
  )
}

async function fetchEventAudit(eventId: number, limit: number): Promise<AuditEntry[]> {
  const res = await fetch(`/api/events/${eventId}/audit?limit=${limit}`, { credentials: 'include' })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    throw new Error(`${res.status} ${res.statusText}: ${text}`)
  }
  return res.json()
}

// ---- KV helper -----------------------------------------------------------

function KV({
  label, value, last
}: {
  label: string
  value: React.ReactNode
  last?: boolean
}) {
  return (
    <div
      className="grid items-center gap-3 py-2.5"
      style={{
        gridTemplateColumns: '160px 1fr',
        borderBottom: last ? 'none' : '1px solid #ece6db'
      }}
    >
      <div className="text-[10px] uppercase tracking-wider font-semibold text-ink-soft">
        {label}
      </div>
      <div className="text-sm font-semibold">{value}</div>
    </div>
  )
}

function Muted() {
  return <span className="italic text-ink-soft font-normal">—</span>
}

// ---- Helpers -------------------------------------------------------------

// Inclusive day-count: a one-day event (start === end) returns 1, not 0.
function daysInclusive(startIso: string, endIso: string): number {
  try {
    const s = parseIsoDate(startIso)
    const e = parseIsoDate(endIso)
    return Math.max(1, Math.round((e.getTime() - s.getTime()) / 86_400_000) + 1)
  } catch {
    return 0
  }
}

// Days from today to the start date. Negative = past, 0 = today, positive
// = future. Uses UTC midnight on both sides so we don't shimmer with the
// hour of day.
function daysFromToday(startIso: string): number {
  try {
    const s = parseIsoDate(startIso)
    const now = new Date()
    const today = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()))
    return Math.round((s.getTime() - today.getTime()) / 86_400_000)
  } catch {
    return 0
  }
}

// "2 hours ago" / "Yesterday" / "3 days ago" / "2 weeks ago" / "1 month
// ago". Coarse; granularity matches what Chris saw in the v5 prototype.
function relativeTime(iso: string): string {
  const t = new Date(iso).getTime()
  if (Number.isNaN(t)) return iso
  const secs = Math.max(0, Math.floor((Date.now() - t) / 1000))
  if (secs < 60) return 'Just now'
  const mins = Math.floor(secs / 60)
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`
  const days = Math.floor(hrs / 24)
  if (days === 1) return 'Yesterday'
  if (days < 7) return `${days} days ago`
  const weeks = Math.floor(days / 7)
  if (weeks < 5) return `${weeks} week${weeks === 1 ? '' : 's'} ago`
  const months = Math.floor(days / 30)
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
}
